import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Popover from '@mui/material/Popover';
import Divider from '@mui/material/Divider';
import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import NotificationsOffOutlinedIcon from '@mui/icons-material/NotificationsOffOutlined';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CheckIcon from '@mui/icons-material/Check';

import logo from '../../assets/logo.png';
import { useAuth } from '../../context/AuthContext';
import { usePermissions, ADMIN_SPACE_ID, FINANCE_SPACE_ID } from '../../hooks/usePermissions';
import { useLocalText } from '../common/useLocalText';
import { useNavigate } from 'react-router-dom';
import { useApproval } from '../../context/ApprovalContext';
import { getApprovalById } from '../../api/approvalApi';
import { resolveApprovalTarget } from '../../utils/approvalNavigation';

function timeAgo(date) {
  const s = Math.floor((new Date() - date) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

const NOTIF_CONFIG = {
  requested: {
    accent:  '#3b82f6',
    iconBg:  'rgba(59,130,246,0.08)',
    rowBg:   'rgba(59,130,246,0.03)',
    hoverBg: 'rgba(59,130,246,0.07)',
    label:   'APPROVAL NEEDED',
    icon:    <PendingActionsOutlinedIcon />,
  },
  approved: {
    accent:  '#16a34a',
    iconBg:  'rgba(22,163,74,0.08)',
    rowBg:   'rgba(22,163,74,0.03)',
    hoverBg: 'rgba(22,163,74,0.07)',
    label:   'APPROVED',
    icon:    <CheckCircleOutlineIcon />,
  },
  rejected: {
    accent:  '#dc2626',
    iconBg:  'rgba(220,38,38,0.08)',
    rowBg:   'rgba(220,38,38,0.03)',
    hoverBg: 'rgba(220,38,38,0.07)',
    label:   'REJECTED',
    icon:    <CancelOutlinedIcon />,
  },
};

export default function HomeHeader({ sidebarWidth = 220, isMobile = false, onMenuClick }) {
  const { user, logout, activeSpace, setActiveSpace } = useAuth();
  const { isSystemUser, isGmsAccountant, isGlobalAdmin, isEntityAdmin, systemRole } = usePermissions();
  const navigate = useNavigate();
  const signOutLabel = useLocalText("Sign out");
  const { notifications, unreadCount, markAllRead, markOneRead } = useApproval();

  const [bellAnchor, setBellAnchor] = React.useState(null);
  const [spaceAnchor, setSpaceAnchor] = React.useState(null);

  const bellOpen  = Boolean(bellAnchor);
  const spaceOpen = Boolean(spaceAnchor);

  const handleBellOpen = (e) => {
    setBellAnchor(e.currentTarget);
  };

  const handleNotificationClick = async (n, i) => {
    markOneRead(i);
    setBellAnchor(null);

    if (n.type === 'requested') {
      navigate('/approvals', { state: { openTab: 'inbox', highlightApprovalId: n.approvalId } });
      return;
    }

    if (!n.approvalId) {
      navigate('/approvals');
      return;
    }

    try {
      const full = await getApprovalById(n.approvalId);
      const { pathname, state } = resolveApprovalTarget(full?.contentType);
      navigate(pathname, state ? { state } : undefined);
    } catch {
      navigate('/approvals');
    }
  };

  const spaces = user?.spaces ?? [];
  const ADMIN_ENTRY   = { spaceId: ADMIN_SPACE_ID,  roles: [], _isAdmin:   true };
  const FINANCE_ENTRY = { spaceId: FINANCE_SPACE_ID, roles: [], _isFinance: true };
  const portalEntry   = isGmsAccountant
    ? FINANCE_ENTRY
    : (isGlobalAdmin || isEntityAdmin) ? ADMIN_ENTRY : null;
  const switcherList  = portalEntry ? [portalEntry, ...spaces] : spaces;
  const canSwitch    = switcherList.length > 1;
  const canOpen       = switcherList.length >= 1;
  const activeRoleLabel  = (activeSpace?.roles ?? [])
    .map((r) => r.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()))
    .join(', ');

  const handleSpaceSwitch = (space) => {
    setSpaceAnchor(null);
    if (space.spaceId === activeSpace?.spaceId) return;
    // Navigate first and defer the state update by one tick.
    // This guarantees the /admin route (and its ProtectedRoute segment check)
    // is unmounted before activeSpace changes to the new (possibly unpermitted) space.
    navigate('/', { replace: true });
    setTimeout(() => setActiveSpace(space), 1);
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        background: "#0a326d",
        left: isMobile ? 0 : sidebarWidth,
        width: isMobile ? '100%' : `calc(100% - ${sidebarWidth}px)`,
        transition: "left 0.2s ease, width 0.2s ease",
        borderBottom: "1px solid rgba(255,255,255,0.08)",
        boxShadow: "0 2px 16px rgba(10,50,109,0.18)",
      }}
    >
      <Toolbar sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 3, minHeight: 64 }}>

        {/* Hamburger — mobile only */}
        {isMobile && (
          <IconButton
            color="inherit"
            edge="start"
            onClick={onMenuClick}
            sx={{ mr: 1, color: "white" }}
            aria-label="open navigation"
          >
            <MenuIcon />
          </IconButton>
        )}

        {/* LEFT — Logo */}
        <Box
          component="img"
          src={logo}
          alt="Company Logo"
          sx={{ height: 38, objectFit: "contain", flexShrink: 0 }}
        />

        {/* RIGHT — all controls */}
        {user && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>

            {/* Notification bell */}
            <Tooltip title="Notifications">
              <IconButton
                onClick={handleBellOpen}
                sx={{
                  color: bellOpen ? "white" : "rgba(255,255,255,0.8)",
                  p: 1,
                  bgcolor: bellOpen ? "rgba(255,255,255,0.15)" : "transparent",
                  borderRadius: "50%",
                  transition: "all 0.2s ease",
                  "&:hover": { color: "white", bgcolor: "rgba(255,255,255,0.1)" },
                }}
              >
                <Badge
                  badgeContent={unreadCount}
                  max={9}
                  color="error"
                  sx={{ '& .MuiBadge-badge': { fontSize: 10, minWidth: 16, height: 16, top: 2, right: 2 } }}
                >
                  {unreadCount > 0
                    ? <NotificationsActiveIcon sx={{ fontSize: 22 }} />
                    : <NotificationsNoneIcon   sx={{ fontSize: 22 }} />
                  }
                </Badge>
              </IconButton>
            </Tooltip>

            {/* Notifications popover */}
            <Popover
              open={bellOpen}
              anchorEl={bellAnchor}
              onClose={() => setBellAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
              transformOrigin={{ vertical: "top", horizontal: "center" }}
              slotProps={{
                paper: {
                  sx: {
                    mt: 1.5,
                    width: { xs: 'calc(100vw - 24px)', sm: 380 },
                    maxWidth: 380,
                    maxHeight: { xs: 'calc(100vh - 120px)', sm: 480 },
                    overflowY: 'auto',
                    borderRadius: '16px',
                    boxShadow: '0 20px 60px rgba(10,50,109,0.25), 0 4px 16px rgba(0,0,0,0.1)',
                    border: '1px solid rgba(83,131,179,0.12)',
                  },
                },
              }}
            >
              {/* Header */}
              <Box sx={{
                px: 2.5,
                py: 2,
                background: 'linear-gradient(135deg, #0a326d 0%, #0d3d87 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid rgba(255,255,255,0.07)',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{
                    width: 38,
                    height: 38,
                    borderRadius: '10px',
                    bgcolor: 'rgba(255,255,255,0.12)',
                    border: '1px solid rgba(255,255,255,0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    {unreadCount > 0
                      ? <NotificationsActiveIcon sx={{ fontSize: 19, color: 'white' }} />
                      : <NotificationsNoneIcon sx={{ fontSize: 19, color: 'rgba(255,255,255,0.7)' }} />
                    }
                  </Box>
                  <Box>
                    <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.9375rem', lineHeight: 1.2 }}>
                      Notifications
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.6875rem', mt: 0.2, lineHeight: 1 }}>
                      {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
                    </Typography>
                  </Box>
                </Box>
                {notifications.length > 0 && (
                  <Button
                    size="small"
                    onClick={markAllRead}
                    sx={{
                      textTransform: 'none',
                      fontSize: '0.6875rem',
                      fontWeight: 600,
                      color: 'rgba(255,255,255,0.8)',
                      bgcolor: 'rgba(255,255,255,0.1)',
                      border: '1px solid rgba(255,255,255,0.15)',
                      borderRadius: '20px',
                      px: 1.5,
                      py: 0.5,
                      lineHeight: 1.4,
                      minWidth: 0,
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.2)', color: 'white', borderColor: 'rgba(255,255,255,0.3)' },
                      transition: 'all 0.18s ease',
                    }}
                  >
                    Mark all read
                  </Button>
                )}
              </Box>

              {/* List or empty state */}
              {notifications.length === 0 ? (
                <Box sx={{ py: 5, px: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, bgcolor: '#ffffff' }}>
                  <Box sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, rgba(10,50,109,0.06) 0%, rgba(83,131,179,0.1) 100%)',
                    border: '1px solid rgba(83,131,179,0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <NotificationsOffOutlinedIcon sx={{ fontSize: 26, color: '#5383b3', opacity: 0.7 }} />
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" fontWeight={700} color="#1e293b" sx={{ mb: 0.5 }}>
                      You're all caught up
                    </Typography>
                    <Typography variant="caption" color="#94a3b8" sx={{ lineHeight: 1.6, display: 'block' }}>
                      No new notifications right now.<br />We'll alert you when something needs attention.
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box sx={{
                  maxHeight: 340,
                  overflowY: 'auto',
                  bgcolor: '#ffffff',
                  '&::-webkit-scrollbar': { width: 4 },
                  '&::-webkit-scrollbar-track': { background: 'transparent' },
                  '&::-webkit-scrollbar-thumb': { background: 'rgba(83,131,179,0.25)', borderRadius: 4 },
                }}>
                  {notifications.map((n, i) => {
                    const text = n.type === 'requested'
                      ? `${n.label} needs your approval${n.name ? ` — ${n.name}` : ''}`
                      : n.type === 'approved'
                        ? `Your ${n.label} "${n.name}" was approved`
                        : `Your ${n.label} "${n.name}" was rejected${n.reason ? `: ${n.reason}` : ''}`;

                    const cfg = NOTIF_CONFIG[n.type];

                    return (
                      <Box
                        key={i}
                        onClick={() => handleNotificationClick(n, i)}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1.5,
                          px: 2,
                          py: 1.75,
                          cursor: 'pointer',
                          borderLeft: `3px solid ${cfg.accent}`,
                          bgcolor: cfg.rowBg,
                          borderBottom: i < notifications.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none',
                          transition: 'background 0.18s ease',
                          '&:hover': { bgcolor: cfg.hoverBg },
                          '&:hover .notif-close': { opacity: 1 },
                        }}
                      >
                        {/* Icon card */}
                        <Box sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '10px',
                          bgcolor: cfg.iconBg,
                          border: `1px solid ${cfg.accent}22`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          mt: 0.25,
                        }}>
                          {React.cloneElement(cfg.icon, { sx: { fontSize: 17, color: cfg.accent } })}
                        </Box>

                        {/* Content */}
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Box sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            px: 1,
                            py: 0.25,
                            mb: 0.6,
                            bgcolor: cfg.iconBg,
                            border: `1px solid ${cfg.accent}30`,
                            borderRadius: '20px',
                          }}>
                            <Typography sx={{ fontSize: '0.5625rem', fontWeight: 700, color: cfg.accent, letterSpacing: '0.7px', lineHeight: 1 }}>
                              {cfg.label}
                            </Typography>
                          </Box>
                          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500, color: '#1e293b', lineHeight: 1.45, wordBreak: 'break-word' }}>
                            {text}
                          </Typography>
                          <Typography sx={{ fontSize: '0.6875rem', color: '#94a3b8', mt: 0.5, display: 'block' }}>
                            {timeAgo(n.at)}
                          </Typography>
                        </Box>

                        {/* Close — appears on row hover */}
                        <IconButton
                          className="notif-close"
                          size="small"
                          onClick={(e) => { e.stopPropagation(); markOneRead(i); }}
                          sx={{
                            flexShrink: 0,
                            opacity: 0,
                            transition: 'opacity 0.18s ease',
                            color: '#94a3b8',
                            alignSelf: 'flex-start',
                            mt: 0.25,
                            p: 0.5,
                            '&:hover': { color: '#475569', bgcolor: 'rgba(0,0,0,0.06)' },
                          }}
                        >
                          <CloseOutlinedIcon sx={{ fontSize: 13 }} />
                        </IconButton>
                      </Box>
                    );
                  })}
                </Box>
              )}

              {/* Footer */}
              <Box sx={{ px: 2, py: 1.5, bgcolor: '#f8fafc', borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                <Button
                  fullWidth
                  size="small"
                  onClick={() => { setBellAnchor(null); navigate('/approvals'); }}
                  endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 15 }} />}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    color: '#0a326d',
                    bgcolor: 'rgba(10,50,109,0.06)',
                    borderRadius: '10px',
                    py: 1,
                    '&:hover': { bgcolor: 'rgba(10,50,109,0.1)' },
                    transition: 'all 0.18s ease',
                  }}
                >
                  View All Approvals
                </Button>
              </Box>
            </Popover>

            {/* Space switcher */}
            {activeSpace && (
              <>
                <Tooltip title={canSwitch ? "Switch space" : (activeRoleLabel || (activeSpace.spaceName || `Space ${activeSpace.spaceId}`))}>
                  <Box
                    onClick={canOpen ? (e) => setSpaceAnchor(e.currentTarget) : undefined}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      px: 1.5,
                      py: 0.75,
                      borderRadius: 2,
                      border: "1px solid rgba(255,255,255,0.18)",
                      bgcolor: spaceOpen ? "rgba(255,255,255,0.18)" : "rgba(255,255,255,0.09)",
                      cursor: canOpen ? "pointer" : "default",
                      transition: "all 0.18s ease",
                      userSelect: "none",
                      "&:hover": canOpen
                        ? { bgcolor: "rgba(255,255,255,0.18)", borderColor: "rgba(255,255,255,0.32)" }
                        : {},
                    }}
                  >
                    {activeSpace.spaceId === ADMIN_SPACE_ID
                      ? <AdminPanelSettingsOutlinedIcon sx={{ fontSize: 16, color: "rgba(255,255,255,0.75)" }} />
                      : activeSpace.spaceId === FINANCE_SPACE_ID
                        ? <AccountBalanceOutlinedIcon sx={{ fontSize: 16, color: "rgba(255,255,255,0.75)" }} />
                        : <LayersOutlinedIcon sx={{ fontSize: 16, color: "rgba(255,255,255,0.75)" }} />
                    }
                    <Typography variant="caption" fontWeight={700} sx={{ color: "white", letterSpacing: 0.4, lineHeight: 1 }}>
                      {activeSpace.spaceId === ADMIN_SPACE_ID
                        ? "Admin"
                        : activeSpace.spaceId === FINANCE_SPACE_ID
                          ? "Finance"
                          : (activeSpace.spaceName || `Space ${activeSpace.spaceId}`)}
                    </Typography>
                    {canOpen && (
                      <ExpandMoreIcon
                        sx={{
                          fontSize: 15,
                          color: "rgba(255,255,255,0.6)",
                          transform: spaceOpen ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "transform 0.2s ease",
                        }}
                      />
                    )}
                  </Box>
                </Tooltip>

                {/* Space switcher dropdown */}
                <Popover
                  open={spaceOpen}
                  anchorEl={spaceAnchor}
                  onClose={() => setSpaceAnchor(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  slotProps={{
                    paper: {
                      sx: {
                        mt: 1.5,
                        width: { xs: 'calc(100vw - 24px)', sm: 300 },
                        maxWidth: 300,
                        maxHeight: { xs: 'calc(100vh - 120px)', sm: 500 },
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        borderRadius: '16px',
                        boxShadow: '0 20px 60px rgba(10,50,109,0.25), 0 4px 16px rgba(0,0,0,0.1)',
                        border: '1px solid rgba(83,131,179,0.12)',
                      },
                    },
                  }}
                >
                  {/* Header */}
                  <Box sx={{
                    px: 2.5,
                    py: 2,
                    background: 'linear-gradient(135deg, #0a326d 0%, #0d3d87 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    borderBottom: '1px solid rgba(255,255,255,0.07)',
                  }}>
                    <Box sx={{
                      width: 38,
                      height: 38,
                      borderRadius: '10px',
                      bgcolor: 'rgba(255,255,255,0.12)',
                      border: '1px solid rgba(255,255,255,0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <LayersOutlinedIcon sx={{ fontSize: 19, color: 'white' }} />
                    </Box>
                    <Box>
                      <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.9375rem', lineHeight: 1.2 }}>
                        {canSwitch ? 'Switch Space' : 'Space Details'}
                      </Typography>
                      <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.6875rem', mt: 0.2, lineHeight: 1 }}>
                        {canSwitch
                          ? `${switcherList.length} options available`
                          : (activeRoleLabel || 'No roles assigned')}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Space list */}
                  <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5, bgcolor: '#ffffff' }}>
                    {switcherList.map((space, idx) => {
                      const isAdminEntry   = space.spaceId === ADMIN_SPACE_ID;
                      const isFinanceEntry = space.spaceId === FINANCE_SPACE_ID;
                      const isPortalEntry  = isAdminEntry || isFinanceEntry;
                      const isActive       = space.spaceId === activeSpace?.spaceId;
                      const roleLabel      = (space.roles ?? []).map((r) => r.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())).join(', ');
                      const accentColor    = isAdminEntry ? '#7c3aed' : isFinanceEntry ? '#059669' : '#0a326d';
                      const showDivider    = idx > 0 && !isPortalEntry && (switcherList[idx - 1]?.spaceId === ADMIN_SPACE_ID || switcherList[idx - 1]?.spaceId === FINANCE_SPACE_ID);

                      return (
                        <React.Fragment key={space.spaceId}>
                          {showDivider && (
                            <Box sx={{ mx: 0.5, my: 0.5 }}>
                              <Divider sx={{ borderColor: '#f1f5f9' }} />
                            </Box>
                          )}
                          <Box
                            onClick={() => handleSpaceSwitch(space)}
                            sx={{
                              position: 'relative',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                              pl: 1.5,
                              pr: 1.5,
                              py: 1.25,
                              borderRadius: '10px',
                              cursor: isActive ? 'default' : 'pointer',
                              bgcolor: isActive
                                ? isAdminEntry ? 'rgba(99,38,163,0.06)' : isFinanceEntry ? 'rgba(5,150,105,0.06)' : 'rgba(10,50,109,0.06)'
                                : 'transparent',
                              border: isActive
                                ? isAdminEntry ? '1px solid rgba(99,38,163,0.15)' : isFinanceEntry ? '1px solid rgba(5,150,105,0.15)' : '1px solid rgba(10,50,109,0.12)'
                                : '1px solid transparent',
                              transition: 'all 0.15s ease',
                              overflow: 'hidden',
                              '&:hover': !isActive ? {
                                bgcolor: '#f8fafc',
                                border: '1px solid #e2e8f0',
                              } : {},
                            }}
                          >
                            {/* Active left accent bar */}
                            {isActive && (
                              <Box sx={{
                                position: 'absolute',
                                left: 0,
                                top: '15%',
                                bottom: '15%',
                                width: 3,
                                borderRadius: '0 3px 3px 0',
                                bgcolor: accentColor,
                              }} />
                            )}

                            {/* Avatar */}
                            <Box sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '10px',
                              flexShrink: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: isAdminEntry
                                ? isActive
                                  ? 'linear-gradient(135deg, #4c1d95 0%, #7c3aed 100%)'
                                  : 'linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)'
                                : isFinanceEntry
                                  ? isActive
                                    ? 'linear-gradient(135deg, #065f46 0%, #059669 100%)'
                                    : 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)'
                                  : isActive
                                    ? 'linear-gradient(135deg, #0a326d 0%, #1a4d9e 100%)'
                                    : 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                              boxShadow: isActive
                                ? isAdminEntry
                                  ? '0 3px 12px rgba(99,38,163,0.35)'
                                  : isFinanceEntry
                                    ? '0 3px 12px rgba(5,150,105,0.35)'
                                    : '0 3px 12px rgba(10,50,109,0.3)'
                                : 'none',
                              transition: 'all 0.15s ease',
                            }}>
                              {isAdminEntry
                                ? <AdminPanelSettingsOutlinedIcon sx={{ fontSize: 20, color: isActive ? 'white' : '#7c3aed' }} />
                                : isFinanceEntry
                                  ? <AccountBalanceOutlinedIcon sx={{ fontSize: 20, color: isActive ? 'white' : '#059669' }} />
                                  : <LayersOutlinedIcon sx={{ fontSize: 18, color: isActive ? 'white' : '#64748b' }} />
                              }
                            </Box>

                            {/* Info */}
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                              <Typography sx={{
                                fontWeight: isActive ? 700 : 500,
                                fontSize: '0.875rem',
                                color: isActive ? accentColor : '#1e293b',
                                lineHeight: 1.3,
                              }}>
                                {isAdminEntry
                                  ? 'Admin Portal'
                                  : isFinanceEntry
                                    ? 'Finance Portal'
                                    : (space.spaceName || `Space ${space.spaceId}`)}
                              </Typography>
                              <Typography sx={{
                                fontSize: '0.6875rem',
                                color: '#94a3b8',
                                mt: 0.25,
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}>
                                {isPortalEntry
                                  ? (systemRole ? systemRole.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'Portal')
                                  : roleLabel || 'No roles assigned'
                                }
                              </Typography>
                            </Box>

                            {/* Active indicator */}
                            {isActive && (
                              <Box sx={{
                                width: 22,
                                height: 22,
                                borderRadius: '50%',
                                bgcolor: accentColor,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                boxShadow: isAdminEntry
                                  ? '0 2px 6px rgba(99,38,163,0.4)'
                                  : isFinanceEntry
                                    ? '0 2px 6px rgba(5,150,105,0.4)'
                                    : '0 2px 6px rgba(10,50,109,0.35)',
                              }}>
                                <CheckIcon sx={{ fontSize: 13, color: 'white' }} />
                              </Box>
                            )}
                          </Box>
                        </React.Fragment>
                      );
                    })}
                  </Box>
                </Popover>
              </>
            )}

            {/* Pipe divider */}
            <Box sx={{ width: "1px", height: 28, bgcolor: "rgba(255,255,255,0.35)", mx: 0.5, display: { xs: "none", sm: "block" } }} />

            {/* Name + email */}
            <Box sx={{ display: { xs: "none", sm: "flex" }, flexDirection: "column", alignItems: "flex-end" }}>
              <Typography variant="body2" fontWeight={700} color="white" noWrap sx={{ lineHeight: 1.3 }}>
                {user.name}
              </Typography>
              <Typography variant="caption" noWrap sx={{ color: "rgba(255,255,255,0.7)", lineHeight: 1.3 }}>
                {user.email}
              </Typography>
            </Box>

            {/* Logout */}
            <Tooltip title={signOutLabel}>
              <IconButton
                onClick={logout}
                sx={{
                  color: "rgba(255,255,255,0.8)",
                  p: 1,
                  borderRadius: "50%",
                  "&:hover": { color: "white", bgcolor: "rgba(255,255,255,0.1)" },
                }}
              >
                <LogoutIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>

          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
}
